import { select } from "gamedata/GameData";
import { lazy } from "react";
import { z } from "zod";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { gameName } from "~/util/common";
import { tt, useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

const Params = z.object({ npcId: z.string() });

async function makeData(la: OurLoaderArgs) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const npc = la.gd.npcs.get(params.npcId);

  if (npc == null) {
    throw Response.json(
      { msg: t({ en: "NPC not found.", ru: "Монстр не найден." }) },
      { status: 404 },
    );
  }

  const data = {
    npc: select(npc, {
      id: true,
      type: true,
      classes: { select: true },
      npcName: true,
      name: { en: true, ru: true },
      nick: { en: true, ru: true },
      nickColor: true,
      level: true,
      model: {
        meshName: true,
        meshPath: true,
        texturePath: true,
        material: (x) => x,
        animationPath: true,
        animation: true,
        params: { outputBlending: true },
      },
    }),
  };

  return data.npc;
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

const NpcModel = lazy(() => import("~/components/models/NpcModel"));

$PAGE({
  path: "npcs/:npcId/modelviewer",
  loader: $LOADER?.(async (la: OurLoaderArgs) => {
    const data = await makeData(la);
    return { ...data, authUser: la.req.sess?.user };
  }),
  meta: (cx) => {
    const npc = cx.data;
    let nameRu = npc.name.ru;
    let nameEn = npc.name.en;
    const level = npc.level;
    if (npc.nick.en !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;
    if (npc.nick.ru !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;

    const name = cx.t({
      en: `${nameEn} Lvl. ${level}`,
      ru: `${nameRu} (${nameEn}) Ур. ${level}`,
    });

    const game = gameName(cx.realm.game);

    return {
      title: `${cx.t({
        en: "Model Viewer",
        ru: "Просмотр модели",
      })} - ${name} - ${cx.t({
        en: "NPCs",
        ru: "НПЦ",
      })} - ${homePage(cx.realm)}`,
      description: cx.t({
        en: `The 3D model of the NPC ${name} - ${game}. What does ${name} look like?`,
        ru: `3D-модель НПЦ ${name}  - ${game}. Как выглядит ${name}?`,
      }),
    };
  },
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const realm = useRealm();
    const nick = t({ en: data.nick.en, ru: data.nick.ru });
    const name = t({ en: data.name.en, ru: data.name.ru });

    let fullName = name;
    if (nick !== "") {
      fullName = `${nick} ${name}`;
    }
    const npcsTitle = t({ en: "NPCs", ru: "НПЦ" });

    const pageTitle = t({ en: "Model Viewer", ru: "Просмотр модели" });

    const breadcrumbList = [
      { title: npcsTitle, url: turl("/npcs") },
      { title: fullName, url: turl(`/npcs/${data.npcName}`) },
      {
        title: pageTitle,
        url: turl(`/npcs/${data.npcName}/modelviewer`),
      },
    ];
    return (
      <div key={data.id}>
        <div className="content">
          <Breadcrumb items={breadcrumbList} />
          <PageTitle>{pageTitle}</PageTitle>
          {data.model && (
            <div style={{ border: "5px solid #292624", borderRadius: 5 }}>
              <NpcModel
                npc={{
                  meshPath: data.model.meshPath,
                  mesh: data.model.meshName,
                  texturePath: data.model.texturePath,
                  materials: data.model.material,
                  animationPath: data.model.animationPath,
                  animation: data.model.animation,
                  params: data.model.params,
                }}
              />
              <div
                style={{
                  background: "#292624",
                  padding: "10px 4px",
                  color: "#a6a6a6",
                  fontSize: 14,
                }}
              >
                Drag to rotate. Right-click to pan. Scroll to zoom in or out
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
});
